/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IActionContext } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import { DeliveryOption } from '@msdyn365-commerce/retail-proxy';
import React, { useState } from 'react';
import { getDelayedDeliveryMsg } from '../../utilities/estDeliveryDate';
import { ProductArrivalEstimate } from '../../utilities/del-modes-util';
import { IFullOrgUnitAvailability } from '../../actions/utilities/full-org-unit-availability';
import { IBuyboxFindInStoreViewProps } from './get-buybox-find-in-store';

export interface IDeliveryMethodSectionProps {
    // Product availability
    isISO: boolean;
    onlyCNCAvailable: boolean;
    isEcomProductAvailable: boolean;
    isCnCProductAvailable: boolean | undefined;
    isProductAvailable: boolean | undefined;
    isComingSoonProduct: boolean;
    // Delivery selection
    addToCartTiles: (JSX.Element[] | undefined)[] | undefined;
    isHomeDelTileSelected: boolean;
    isCNCTileSelected: boolean;
    // Store selection
    selectedCNCOrgUnitLoc: IFullOrgUnitAvailability | undefined;
    storeNamePrefix?: string;
    // Find in store
    findInStore: IBuyboxFindInStoreViewProps | undefined;
    // Vendor
    vendorName: string | undefined;
    // Postcode & delivery info
    loggedInZipCode: string;
    restrictedPostcodes: string[];
    actionContext: IActionContext;
    deliveryOptions: DeliveryOption[] | undefined;
    cartLineLearnMoreLink: string | undefined;
    // Notify me handler
    onNotifyMeClick: () => void;
    // Loading state - classes will only apply after loading is complete
    isLoading?: boolean;
}

const DeliveryMethodSection: React.FC<IDeliveryMethodSectionProps> = ({
    isISO,
    onlyCNCAvailable,
    isEcomProductAvailable,
    isCnCProductAvailable,
    isProductAvailable,
    isComingSoonProduct,
    addToCartTiles,
    isHomeDelTileSelected,
    isCNCTileSelected,
    selectedCNCOrgUnitLoc,
    storeNamePrefix,
    findInStore,
    vendorName,
    loggedInZipCode,
    restrictedPostcodes,
    actionContext,
    deliveryOptions,
    cartLineLearnMoreLink,
    onNotifyMeClick,
    isLoading
}) => {
    const [showAlternativeInfo, setShowAlternativeInfo] = useState(false);

    if (isComingSoonProduct || (!isISO && !isEcomProductAvailable && !isCnCProductAvailable)) {
        return null;
    }

    const alternateAddressBtn = <Button className='alternate-address' onClick={() => setShowAlternativeInfo(true)} />;

    // Only apply availability classes after loading is complete to prevent layout shift
    const getAvailabilityClass = () => {
        if (isLoading) {
            return '';
        }
        if (!isCnCProductAvailable && !isISO) {
            return 'only-hd-available';
        }
        if (onlyCNCAvailable) {
            return 'only-in-store';
        }
        return '';
    };

    return (
        <div className='ms-buybox__dm'>
            {/* Availability messages - only show after loading */}
            {!isLoading && !isCnCProductAvailable && !isISO ? (
                <p className='only-hd'>Product is only available for Home Delivery.</p>
            ) : !isLoading && onlyCNCAvailable ? (
                <p className='only-cnc'>Product is only available for Click & Collect.</p>
            ) : (
                ''
            )}

            <div className={`ms-buybox__delivery-methods ${getAvailabilityClass()} ${isCNCTileSelected ? 'cnc' : ''}`}>
                {/* Delivery method tiles */}
                <div className='delivery-method-tiles'>{addToCartTiles}</div>

                {/* Prompt to choose delivery method */}
                {!isHomeDelTileSelected && !isCNCTileSelected && (
                    <p className='text-center choose-delivery-method'>Choose your delivery method.</p>
                )}

                {/* Notify me link for home delivery */}
                {!isEcomProductAvailable && !isISO && !selectedCNCOrgUnitLoc && (
                    <div className='d-flex justify-content-center mt-2'>
                        <a
                            className='email-me'
                            onClick={onNotifyMeClick}
                            color='secondary'
                            aria-label='Email me when available for Home Delivery'
                        >
                            <span className='msc-in-stock-red'></span>
                            Email me when available for Home Delivery
                        </a>
                    </div>
                )}

                {/* Home delivery selected info */}
                {!isISO && isHomeDelTileSelected && (isProductAvailable || vendorName) && (
                    <HomeDeliveryInfo
                        vendorName={vendorName}
                        loggedInZipCode={loggedInZipCode}
                        restrictedPostcodes={restrictedPostcodes}
                        showAlternativeInfo={showAlternativeInfo}
                        setShowAlternativeInfo={setShowAlternativeInfo}
                        alternateAddressBtn={alternateAddressBtn}
                        actionContext={actionContext}
                        deliveryOptions={deliveryOptions}
                    />
                )}

                {/* Click & Collect selected info */}
                {isCNCTileSelected && (isCnCProductAvailable || isISO) && (
                    <CnCSelectedInfo
                        selectedCNCOrgUnitLoc={selectedCNCOrgUnitLoc}
                        storeNamePrefix={storeNamePrefix}
                        findInStore={findInStore}
                        cartLineLearnMoreLink={cartLineLearnMoreLink}
                    />
                )}
            </div>
        </div>
    );
};

// Sub-component for Home Delivery info
interface IHomeDeliveryInfoProps {
    vendorName: string | undefined;
    loggedInZipCode: string;
    restrictedPostcodes: string[];
    showAlternativeInfo: boolean;
    setShowAlternativeInfo: React.Dispatch<React.SetStateAction<boolean>>;
    alternateAddressBtn: JSX.Element;
    actionContext: IActionContext;
    deliveryOptions: DeliveryOption[] | undefined;
}

const HomeDeliveryInfo: React.FC<IHomeDeliveryInfoProps> = ({
    vendorName,
    loggedInZipCode,
    restrictedPostcodes,
    showAlternativeInfo,
    setShowAlternativeInfo,
    alternateAddressBtn,
    actionContext,
    deliveryOptions
}) => {
    const delayedDeliveryMsg = getDelayedDeliveryMsg(actionContext, loggedInZipCode);
    const hasRestrictedPostcode = restrictedPostcodes.some(postcode => loggedInZipCode.startsWith(postcode));

    return (
        <div className='home-del-selected'>
            {hasRestrictedPostcode && (
                <div className='notification-alert notification-alert__yellow'>
                    <div className='notification-alert__message'>
                        <p className='notification-alert__message--paragraph'>
                            This item cannot be delivered to some postcodes. Please click <a href='/delivery-information'>here</a> for more
                            details.
                        </p>
                    </div>
                </div>
            )}
            <p className='dispatched-by'>
                <a href={vendorName ? '/approved-suppliers' : '/delivery-information'} target='_blank'>
                    <span>Dispatched by: {vendorName || 'Dobbies'}</span>
                </a>
            </p>
            {loggedInZipCode && (
                <div className='d-flex justify-content-center'>
                    <div className={`alternative-address-info ${showAlternativeInfo ? 'show' : ''}`}>
                        <button onClick={() => setShowAlternativeInfo(false)} className='alternative-address-info--close' />
                        <p>
                            An alternative delivery address can be <br /> added at checkout.
                        </p>
                    </div>
                </div>
            )}
            <div className='w-100'>
                <ProductArrivalEstimate
                    actionContext={actionContext}
                    loggedInZipCode={loggedInZipCode}
                    alternateAddressBtn={alternateAddressBtn}
                    deliveryOptions={deliveryOptions}
                />
            </div>
            <p className='delivery-fee'>If delivery fees apply, you'll see them in your basket before checkout.</p>
            {loggedInZipCode && delayedDeliveryMsg && <p className='delivery-note'>{delayedDeliveryMsg}</p>}
        </div>
    );
};

// Sub-component for CnC Selected info
interface ICnCSelectedInfoProps {
    selectedCNCOrgUnitLoc: IFullOrgUnitAvailability | undefined;
    storeNamePrefix?: string;
    findInStore: IBuyboxFindInStoreViewProps | undefined;
    cartLineLearnMoreLink: string | undefined;
}

const CnCSelectedInfo: React.FC<ICnCSelectedInfoProps> = ({
    selectedCNCOrgUnitLoc,
    storeNamePrefix,
    findInStore,
    cartLineLearnMoreLink
}) => {
    return (
        <div className='cnc-selected'>
            {selectedCNCOrgUnitLoc && (
                <div>
                    In-stock:
                    <b className='ml-1 mr-1'>
                        {storeNamePrefix}
                        {selectedCNCOrgUnitLoc.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName}.
                    </b>
                    <a
                        className='choose-store'
                        onClick={() => {
                            if (findInStore?.clickCNC) {
                                findInStore.clickCNC();
                            }
                        }}
                    >
                        Change store
                    </a>
                </div>
            )}
            <p>
                We'll email when your order is ready.{' '}
                <a href={cartLineLearnMoreLink || '#'} target='_blank'>
                    Learn more
                </a>
            </p>
        </div>
    );
};

export default DeliveryMethodSection;
